import { useEffect, useRef, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { Layout } from "../../components/layout/Layout";
import { DocumentHeader } from "../../components/DocumentHeader";
import { SignaturePad } from "../../components/SignaturePad";
import { DocumentClosureBar } from "../../components/DocumentClosureBar";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Button } from "../../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../../components/ui/select";
import { api, formatApiError } from "../../lib/api";
import { useAuth, hasPerm } from "../../context/AuthContext";
import { toast } from "sonner";
import { Printer, Save, Mail, Link2, Copy, ClipboardCheck, FileDown, ShieldQuestion } from "lucide-react";
import { DocumentImagesGallery } from "../../components/DocumentImagesGallery";
import { DiagnosticTestsEditor } from "../../components/DiagnosticTestsEditor";

export default function InterventionFormPage() {
  const { id } = useParams();
  const isNew = id === "new";
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const sigRef = useRef(null);
  const motifSigRef = useRef(null);
  const [shop, setShop] = useState(null);
  const [mailOpen, setMailOpen] = useState(false);
  const [mailTo, setMailTo] = useState("");
  const [mailSubject, setMailSubject] = useState("");
  const [mailMessage, setMailMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [suiviStatuts, setSuiviStatuts] = useState([]);
  const [suiviSaving, setSuiviSaving] = useState(false);
  const [data, setData] = useState({
    numero: "",
    client_id: searchParams.get("client_id") || null,
    client_nom: searchParams.get("client_nom") || "", client_tel: searchParams.get("client_tel") || "",
    client_email: searchParams.get("client_email") || "", client_adresse: searchParams.get("client_adresse") || "",
    materiel: "", imei: "", date_rdv: "", motif: "", intervention_effectuee: "", tests: {},
  });

  useEffect(() => {
    (async () => {
      const shopsRes = await api.get("/shops");
      const myShop = shopsRes.data.find((s) => s.id === user.effective_shop_id);
      if (!myShop && isNew) {
        toast.error("Aucune boutique n'est assignée à votre compte. Contactez un administrateur.");
      }
      setShop(myShop || shopsRes.data[0]);
      if (!isNew) {
        const { data: item } = await api.get(`/interventions/${id}`);
        setData(item);
      }
    })();
    api.get("/interventions/suivi-statuts").then((r) => setSuiviStatuts(r.data)).catch(() => {});
  }, [id]);

  const suiviLink = data.suivi_token ? `${window.location.origin}/suivi/${data.suivi_token}` : "";

  const copySuiviLink = async () => {
    try {
      await navigator.clipboard.writeText(suiviLink);
      toast.success("Lien de suivi copié");
    } catch {
      toast.error("Impossible de copier le lien automatiquement — sélectionnez-le manuellement");
    }
  };

  const [sendingSuiviLink, setSendingSuiviLink] = useState(false);
  const [stagedMotifImages, setStagedMotifImages] = useState([]);
  const [diagnosticOpen, setDiagnosticOpen] = useState(false);
  const [stagedResultatImages, setStagedResultatImages] = useState([]);
  const sendSuiviLinkByEmail = async () => {
    if (!data.client_email) {
      toast.error("Renseignez l'adresse e-mail du client avant d'envoyer le lien");
      return;
    }
    setSendingSuiviLink(true);
    try {
      const res = await api.post(`/interventions/${id}/send-suivi-link`, { suivi_link: suiviLink });
      toast.success(`Lien de suivi envoyé à ${res.data.sent_to}`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSendingSuiviLink(false);
    }
  };

  const updateSuiviStatut = async (value) => {
    setSuiviSaving(true);
    try {
      const { data: updated } = await api.patch(`/interventions/${id}/suivi`, { suivi_statut: value });
      setData(updated);
      toast.success("Statut de suivi mis à jour");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSuiviSaving(false);
    }
  };

  const today = new Date().toLocaleDateString("fr-FR");

  const hasEditPerm = data.is_shared_to_me
    ? data.share_mode === "write"
    : (isNew ? hasPerm(user, "intervention", "create") : hasPerm(user, "intervention", "edit"));
  const isCloturee = data.status === "cloture";
  // Le motif est verrouillé dès que sa signature dédiée est posée, indépendamment
  // de la clôture globale du document (qui, elle, verrouille tout le reste).
  const isMotifLocked = !!data.motif_signature_at;
  const canEdit = hasEditPerm && !isCloturee;
  const canEditMotif = canEdit && !isMotifLocked;

  const save = async () => {
    const signature_data = sigRef.current?.toDataURL() || data.signature_data || "";
    const motif_signature_data = motifSigRef.current?.toDataURL() || data.motif_signature_data || "";
    if (isNew && !shop) {
      toast.error("Impossible de créer : aucune boutique assignée à votre compte.");
      return;
    }
    try {
      if (isNew) {
        const { data: created } = await api.post("/interventions", { ...data, shop_id: shop.id, signature_data, motif_signature_data });
        // Bug corrigé : il fallait obligatoirement enregistrer avant de
        // pouvoir ajouter la moindre photo. Les photos choisies AVANT la
        // création (mode brouillon) sont désormais envoyées automatiquement
        // ici, juste après la création réelle du document — une file par
        // section (motif / intervention effectuée), pour l'avant/après.
        const envoiParSection = async (files, section) => {
          for (const file of files) {
            const fd = new FormData();
            fd.append("file", file);
            fd.append("section", section);
            try {
              await api.post(`/interventions/${created.id}/images`, fd);
            } catch (imgErr) {
              toast.error(`Une image n'a pas pu être enregistrée : ${formatApiError(imgErr.response?.data?.detail)}`);
            }
          }
        };
        await envoiParSection(stagedMotifImages, "motif");
        await envoiParSection(stagedResultatImages, "resultat");
        toast.success(`Intervention ${created.numero} créée`);
        navigate(`/interventions/${created.id}`);
      } else {
        const { data: updated } = await api.patch(`/interventions/${id}`, { ...data, signature_data, motif_signature_data });
        setData(updated);
        toast.success("Intervention mise à jour");
      }
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const openMailDialog = () => {
    setMailTo(data.client_email || "");
    setMailSubject(`Votre fiche d'intervention ${data.numero || ""} - ${shop?.nom || ""}`);
    setMailMessage(`Bonjour ${data.client_nom || ""},\n\nVeuillez trouver ci-joint votre fiche d'intervention ${data.numero || ""}.\n\nCordialement,\n${shop?.nom || ""}`);
    setMailOpen(true);
  };

  const sendMail = async () => {
    if (!mailTo) { toast.error("Saisissez l'adresse e-mail du client"); return; }
    setSending(true);
    try {
      const res = await api.post(`/interventions/${id}/send-email`, { to_email: mailTo, subject: mailSubject, message: mailMessage });
      toast.success(`Fiche envoyée à ${res.data.sent_to}`);
      setMailOpen(false);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  return (
    <Layout title="Fiche d'intervention">
      <div className="a4-sheet max-w-4xl mx-auto p-4 sm:p-8 md:p-12 rounded-md">
        <DocumentHeader shop={shop} numero={data.numero || "(auto)"} />
        {data.is_shared_to_me && (
          <p className="text-xs text-center text-muted-foreground mb-4" data-testid="intervention-shared-banner">
            Document partagé par {data.shared_by_label} — {data.share_mode === "write" ? "lecture / écriture" : "lecture seule"}
          </p>
        )}
        <h2 className="font-heading text-xl font-bold text-center mb-6">FICHE D'INTERVENTION</h2>

        <DocumentClosureBar
          module="interventions"
          id={isNew ? null : id}
          data={data}
          canEdit={hasEditPerm}
          canDelete={hasPerm(user, "intervention", "delete")}
          onUpdated={setData}
        />

        {!isNew && data.suivi_token && (
          <div className="rounded-xl border border-border bg-secondary/40 p-4 mb-6 flex flex-col sm:flex-row sm:items-center gap-3" data-testid="intervention-suivi-card">
            <div className="flex items-center gap-2 shrink-0">
              <Link2 size={15} className="text-muted-foreground" />
              <span className="text-xs font-medium text-muted-foreground whitespace-nowrap">Suivi client</span>
            </div>
            <Select value={data.suivi_statut || "recu"} onValueChange={updateSuiviStatut} disabled={suiviSaving || !hasEditPerm}>
              <SelectTrigger data-testid="intervention-suivi-statut" className="w-full sm:w-56"><SelectValue /></SelectTrigger>
              <SelectContent>
                {suiviStatuts.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
              </SelectContent>
            </Select>
            <Button data-testid="intervention-suivi-copy" type="button" size="sm" variant="outline" className="gap-1.5 shrink-0" onClick={copySuiviLink}>
              <Copy size={13} /> Copier le lien de suivi
            </Button>
            <Button data-testid="intervention-suivi-send-mail" type="button" size="sm" variant="outline" className="gap-1.5 shrink-0" onClick={sendSuiviLinkByEmail} disabled={sendingSuiviLink}>
              <Mail size={13} /> {sendingSuiviLink ? "Envoi..." : "Envoyer le lien par e-mail"}
            </Button>
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6 text-sm">
          <div><span className="font-semibold">Vendeur:</span> {data.vendeur_nom || `${user.prenom} ${user.nom}`}</div>
          <div><span className="font-semibold">Date:</span> {data.date ? `${data.date}${data.heure ? " " + data.heure : ""}` : today}</div>
          <Input data-testid="int-client-nom" disabled={!canEdit} placeholder="Nom / prénom client" value={data.client_nom} onChange={(e) => setData({ ...data, client_nom: e.target.value })} />
          <Input data-testid="int-client-tel" disabled={!canEdit} placeholder="Téléphone client" value={data.client_tel} onChange={(e) => setData({ ...data, client_tel: e.target.value })} />
          <Input data-testid="int-client-email" disabled={!canEdit} placeholder="Email client" value={data.client_email} onChange={(e) => setData({ ...data, client_email: e.target.value })} />
          <Input data-testid="int-client-adresse" disabled={!canEdit} placeholder="Adresse client" value={data.client_adresse} onChange={(e) => setData({ ...data, client_adresse: e.target.value })} />
          <Input data-testid="int-materiel" disabled={!canEdit} placeholder="Matériel concerné" value={data.materiel} onChange={(e) => setData({ ...data, materiel: e.target.value })} />
          <Input data-testid="int-imei" disabled={!canEdit} placeholder="IMEI" value={data.imei} onChange={(e) => setData({ ...data, imei: e.target.value })} />
          <div className="sm:col-span-2 space-y-1">
            <label className="text-xs font-medium text-muted-foreground">Rendez-vous client (optionnel)</label>
            <Input
              data-testid="int-date-rdv"
              type="datetime-local"
              disabled={!canEdit}
              value={data.date_rdv || ""}
              onChange={(e) => setData({ ...data, date_rdv: e.target.value })}
            />
            <p className="text-[10px] text-muted-foreground">Affiché dans l'agenda de la semaine du tableau de bord.</p>
          </div>
        </div>

        <label className="text-sm font-semibold">Motif de l'intervention</label>
        <Textarea data-testid="int-motif" disabled={!canEditMotif} className="mb-2 h-28" value={data.motif} onChange={(e) => setData({ ...data, motif: e.target.value })} />

        <Button
          type="button" variant="outline" size="sm" className="gap-2 mb-4"
          onClick={() => setDiagnosticOpen(true)} data-testid="int-diagnostic-button"
        >
          <ClipboardCheck size={14} />Diagnostic / phase de test
          {Object.keys(data.tests || {}).length > 0 && (
            <span className="text-xs text-muted-foreground">({Object.keys(data.tests).length})</span>
          )}
        </Button>

        {!isNew && hasPerm(user, "sav", "create") && (
          <Button
            type="button" variant="outline" size="sm" className="gap-2 mb-4 ml-2"
            onClick={() => navigate("/sav/new", {
              state: {
                from_intervention: {
                  id, numero: data.numero, client_nom: data.client_nom, client_tel: data.client_tel,
                  client_email: data.client_email, produit_nom: data.materiel, imei: data.imei,
                  date_achat: data.created_at,
                  // Demande explicite : le motif ET l'intervention effectuée
                  // sont repris ensemble dans le motif du SAV créé.
                  motif: [
                    data.motif ? `Motif de l'intervention :\n${data.motif}` : "",
                    data.intervention_effectuee ? `Intervention effectuée :\n${data.intervention_effectuee}` : "",
                  ].filter(Boolean).join("\n\n"),
                },
              },
            })}
            data-testid="int-creer-sav-button"
          >
            <ShieldQuestion size={14} />Créer un SAV
          </Button>
        )}

        <DocumentImagesGallery
          module="intervention" itemId={isNew ? null : id} isCloturee={isCloturee} canEdit={hasEditPerm}
          section="motif" title="Photos du motif (avant intervention)"
          stagedFiles={stagedMotifImages} onStagedFilesChange={setStagedMotifImages}
        />

        <label className="text-sm font-semibold block mb-2 mt-4">Signature (motif de l'intervention)</label>
        <SignaturePad ref={motifSigRef} testId="intervention-motif-signature-pad" disabled={!canEdit || isMotifLocked} />
        {isMotifLocked ? (
          <p className="text-xs text-muted-foreground mt-2 mb-6" data-testid="int-motif-signature-note">
            (signature émise le : {data.motif_signature_date} à {data.motif_signature_heure}) — le motif n'est plus modifiable.
          </p>
        ) : (
          <p className="text-xs text-muted-foreground mt-2 mb-6">
            Une fois cette signature posée, le motif ci-dessus sera verrouillé (le document restera modifiable par ailleurs).
          </p>
        )}

        <label className="text-sm font-semibold">Intervention effectuée</label>
        <Textarea data-testid="int-effectuee" disabled={!canEdit} className="mb-2 h-28" value={data.intervention_effectuee} onChange={(e) => setData({ ...data, intervention_effectuee: e.target.value })} />

        <DocumentImagesGallery
          module="intervention" itemId={isNew ? null : id} isCloturee={isCloturee} canEdit={hasEditPerm}
          section="resultat" title="Photos du résultat (après intervention)"
          stagedFiles={stagedResultatImages} onStagedFilesChange={setStagedResultatImages}
        />

        <label className="text-sm font-semibold block mb-2 mt-4">Signature client</label>
        <SignaturePad ref={sigRef} testId="intervention-signature-pad" disabled={!canEdit} />
        {isCloturee && (
          <p className="text-xs text-muted-foreground mt-2">
            Document clôturé : la signature déjà apposée n'est plus modifiable.
          </p>
        )}

        <div className="flex gap-3 mt-6 no-print">
          {canEdit && <Button data-testid="intervention-save-button" onClick={save} className="gap-2"><Save size={16} />Enregistrer</Button>}
          {!isNew && (
            <a href={`${api.defaults.baseURL}/interventions/${id}/pdf`} target="_blank" rel="noreferrer">
              <Button variant="outline" data-testid="intervention-pdf-button" className="gap-2"><Printer size={16} />PDF</Button>
            </a>
          )}
          {!isNew && (
            <Button variant="outline" className="gap-2" onClick={openMailDialog} data-testid="intervention-send-email-button">
              <Mail size={16} />Envoyer par e-mail
            </Button>
          )}
        </div>
      </div>

      <Dialog open={diagnosticOpen} onOpenChange={setDiagnosticOpen}>
        <DialogContent className="max-w-lg" data-testid="intervention-diagnostic-dialog">
          <DialogHeader><DialogTitle>Diagnostic / phase de test</DialogTitle></DialogHeader>
          <DiagnosticTestsEditor
            tests={data.tests}
            onChange={(tests) => setData({ ...data, tests })}
            disabled={!canEditMotif}
          />
          {!isNew && (
            <a
              href={`${api.defaults.baseURL}/interventions/${id}/diagnostic-pdf`} target="_blank" rel="noreferrer"
              className="flex items-center gap-2 text-sm text-primary hover:underline pt-2 border-t border-border"
              data-testid="intervention-diagnostic-pdf-link"
            >
              <FileDown size={15} />Télécharger le PDF de diagnostic
            </a>
          )}
          {isNew && (
            <p className="text-xs text-muted-foreground pt-2 border-t border-border">
              Enregistrez d'abord la fiche pour pouvoir générer le PDF de diagnostic.
            </p>
          )}
          <DialogFooter>
            <Button onClick={() => setDiagnosticOpen(false)} data-testid="intervention-diagnostic-close">Fermer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={mailOpen} onOpenChange={setMailOpen}>
        <DialogContent data-testid="intervention-mail-dialog">
          <DialogHeader><DialogTitle>Envoyer la fiche par e-mail</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Input placeholder="Adresse e-mail du client" value={mailTo} onChange={(e) => setMailTo(e.target.value)} data-testid="intervention-mail-to" />
            <Input placeholder="Objet" value={mailSubject} onChange={(e) => setMailSubject(e.target.value)} data-testid="intervention-mail-subject" />
            <Textarea className="h-32" placeholder="Message" value={mailMessage} onChange={(e) => setMailMessage(e.target.value)} data-testid="intervention-mail-message" />
            <p className="text-xs text-muted-foreground">Le PDF sera joint automatiquement. L'e-mail sera envoyé depuis l'adresse configurée pour votre boutique (Admin &gt; Boutiques &amp; Dépôts).</p>
          </div>
          <DialogFooter>
            <Button onClick={sendMail} disabled={sending} className="gap-2" data-testid="intervention-mail-send-button">
              <Mail size={16} />{sending ? "Envoi..." : "Envoyer"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
